import React, { useEffect, useState } from "react";
import { orders as ordersApi } from "../api/endpoints";
import { DataTableShell, EmptyState, FilterToolbar, MetricStrip, PageHeader } from "../components/Workspace";
import type { Order, OrderStatus } from "../types";

function orderStatusTone(name: string, paid: boolean) {
  if (paid || name === "Paid") return "bg-emerald-100 text-emerald-800";
  if (name.toLowerCase().includes("pending")) return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

export default function OrdersPage() {
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [statuses, setStatuses] = useState<OrderStatus[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status_id", statusFilter);
    ordersApi.list(params.toString()).then((res) => setOrderList(res.items));
    ordersApi.statuses().then(setStatuses);
  }, [search, statusFilter]);

  const totalAmount = orderList.reduce((sum, order) => sum + Number(order.total_amount || 0), 0);
  const paidCount = orderList.filter((order) => order.is_payment_made).length;
  const unpaidCount = orderList.length - paidCount;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Order operations"
        title="Orders"
        description="Review invoice state, purchase order references, and payment status in one dense workspace."
        compact
        badges={[
          { label: `${orderList.length} visible orders`, tone: "brand" },
          { label: `${paidCount} paid`, tone: "success" },
        ]}
      />

      <MetricStrip
        items={[
          { label: "Orders", value: orderList.length, detail: "Visible after current filters", tone: "brand" },
          { label: "Paid", value: paidCount, detail: "Orders marked as paid", tone: "success" },
          { label: "Outstanding", value: unpaidCount, detail: "Orders still awaiting payment", tone: "warning" },
          { label: "Total value", value: `$${totalAmount.toFixed(0)}`, detail: "Current filtered order value", tone: "accent" },
        ]}
      />

      <FilterToolbar sticky className="rise-in delay-1">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),16rem]">
          <input
            placeholder="Search by invoice or PO number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-shell"
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="select-shell">
            <option value="">All statuses</option>
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        </div>
      </FilterToolbar>

      <DataTableShell
        title="Orders ledger"
        subtitle="Payment and invoice state stay visible directly in the table instead of being duplicated across separate summary cards."
        className="rise-in delay-2"
      >
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Order</th>
                <th>Requestor</th>
                <th>Invoice</th>
                <th>PO Number</th>
                <th>Status</th>
                <th className="text-right">Calls</th>
                <th className="text-right">Amount</th>
                <th>Paid</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {orderList.map((order) => (
                <tr key={order.id} className="table-row-button">
                  <td className="font-mono text-sm font-semibold text-slate-900">#{order.id}</td>
                  <td>
                    <div className="font-semibold text-slate-900">{order.requestor_name}</div>
                  </td>
                  <td>{order.invoice_id || "Not assigned"}</td>
                  <td>{order.po_number || "Not assigned"}</td>
                  <td>
                    <span className={`badge-pill ${orderStatusTone(order.status.name, order.is_payment_made)}`}>
                      {order.status.name}
                    </span>
                  </td>
                  <td className="text-right">{order.call_count}</td>
                  <td className="text-right font-semibold text-slate-900">${order.total_amount.toFixed(2)}</td>
                  <td>{order.is_payment_made ? "Yes" : "No"}</td>
                  <td className="text-sm text-slate-600">{new Date(order.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {orderList.length === 0 && (
          <EmptyState title="No Orders" message="Adjust the search or status filter." />
        )}
      </DataTableShell>
    </div>
  );
}
